import React from 'react';
import { NodeToolbar, Position } from 'reactflow';
import { BringToFront, SendToBack, Copy, Trash2, MessageSquare, RotateCw, Pencil } from 'lucide-react';
import { useBoardActions } from '../boardActions';
import { useBoardMultiSelected } from '../useBoardSelection';
import { useBoardComments } from '../comments/BoardCommentsContext';
import { NOTE_COLORS, BOARD_NOTE_FONT_SIZES, BOARD_TEXT_STYLES } from '../boardConstants';

interface NodeActionsToolbarProps {
  id: string;
  /** Show the sticky-note color swatches (note nodes only). */
  showColors?: boolean;
  showFontSize?: boolean;
  fontSize?: number;
  /** Size dropdown options; defaults to the sticky-note range. */
  fontSizeOptions?: readonly number[];
  /** Show the text style preset dropdown (text nodes only). */
  showTextStyles?: boolean;
  textStyle?: string;
  showRotate?: boolean;
  showOpacity?: boolean;
  opacity?: number;
  showEdit?: boolean;
  onEdit?: () => void;
}

/**
 * Floating action bar shown above a node while it is selected: color swatches
 * (notes), font size, z-order, duplicate, and delete.
 */
const NodeActionsToolbar: React.FC<NodeActionsToolbarProps> = ({
  id,
  showColors,
  showFontSize,
  fontSize,
  fontSizeOptions = BOARD_NOTE_FONT_SIZES,
  showTextStyles,
  textStyle,
  showRotate,
  showOpacity,
  opacity,
  showEdit,
  onEdit,
}) => {
  const { updateNodeData, deleteNode, duplicateNode, bringToFront, sendToBack, rotateImage, drawTool, readOnly } =
    useBoardActions();
  const { openComments } = useBoardComments();
  const multiSelected = useBoardMultiSelected();
  if (readOnly || drawTool !== 'select' || multiSelected) return null;

  const currentSize = fontSize || 14;
  const sizeOptions = fontSizeOptions.includes(currentSize)
    ? [...fontSizeOptions]
    : [...fontSizeOptions, currentSize].sort((a, b) => a - b);

  return (
    <NodeToolbar className="board-node-toolbar" position={Position.Top} offset={10}>
      {showColors && (
        <div className="board-note-swatches">
          {NOTE_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className="board-note-swatch"
              style={{ background: color }}
              title="Set color"
              onClick={() => updateNodeData(id, { color })}
            />
          ))}
          <span className="board-toolbar-divider" />
        </div>
      )}
      {showTextStyles && (
        <>
          <select
            className="board-note-font-select nodrag"
            value={textStyle || ''}
            title="Text style"
            onChange={(e) =>
              // Clear the size override so the preset's own size takes effect.
              updateNodeData(id, { textStyle: e.target.value || undefined, fontSize: undefined })
            }
          >
            <option value="">Custom</option>
            {BOARD_TEXT_STYLES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          <span className="board-toolbar-divider" />
        </>
      )}
      {showFontSize && (
        <>
          <select
            className="board-note-font-select nodrag"
            value={currentSize}
            title="Text size"
            onChange={(e) => updateNodeData(id, { fontSize: Number(e.target.value) })}
          >
            {sizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}px
              </option>
            ))}
          </select>
          <span className="board-toolbar-divider" />
        </>
      )}
      {showOpacity && (
        <>
          <label className="board-opacity-control nodrag nowheel" title="Opacity">
            <input
              type="range"
              min={5}
              max={100}
              step={1}
              value={Math.round((opacity ?? 1) * 100)}
              onChange={(e) => updateNodeData(id, { opacity: Number(e.target.value) / 100 })}
            />
            <span>{Math.round((opacity ?? 1) * 100)}%</span>
          </label>
          <span className="board-toolbar-divider" />
        </>
      )}
      {showRotate && (
        <button type="button" title="Rotate 90°" onClick={() => rotateImage(id)}>
          <RotateCw size={15} />
        </button>
      )}
      {showEdit && onEdit && (
        <button type="button" title="Edit" onClick={onEdit}>
          <Pencil size={15} />
        </button>
      )}
      <button type="button" title="Comment" onClick={() => openComments(id)}>
        <MessageSquare size={15} />
      </button>
      <button type="button" title="Bring to front" onClick={() => bringToFront(id)}>
        <BringToFront size={15} />
      </button>
      <button type="button" title="Send to back" onClick={() => sendToBack(id)}>
        <SendToBack size={15} />
      </button>
      <button type="button" title="Duplicate" onClick={() => duplicateNode(id)}>
        <Copy size={15} />
      </button>
      <button type="button" title="Delete" className="board-toolbar-danger" onClick={() => deleteNode(id)}>
        <Trash2 size={15} />
      </button>
    </NodeToolbar>
  );
};

export default NodeActionsToolbar;
